import { buildLoginPayload, confirmSignUp as amplifyConfirmSignUp, signIn } from '../utils/amplifyAuthHelpers';
import cleanDeep from 'clean-deep';
import _ from 'lodash';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import * as yup from 'yup';
import { Actions as AUTHENTICATION_ACTIONS } from '../../store/actions/authenticationActions';
import { Actions as ORGANISATIONACTIONS } from '../../store/actions/organisationActions';
import { Actions as PAYMENTACTIONS } from '../../store/actions/paymentActions';
import { Actions as LEGAL_ACTIONS } from '../../store/actions/legalActions';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import LogoSide from './LogoSide';
import ValidationMessage from '../utils/ValidationMessage';
import { parseTrialSignupParams, trialRegistrationErrorMessage } from '../utils/subscriptionAccess';
import BillingDetails from './BillingDetails';
import RegistrationAgreements from './RegistrationAgreements';

const signupValidationSchema = yup.object().shape({
  acceptTNCs: yup
    .boolean()
    .required()
    .oneOf([true], 'Accepting the Software License Agreement & Privacy Policy is required'),
});

const ConfirmEmail = ({
  username,
  branding,
  password,
  getUserCredentials,
  user,
  organisation,
  newUser,
  currentOrganisation,
  loggingIn,
  processing,
  newConfirmationCode,
  newPassword,
  newUserSubscription,
  getOrganisation,
  setPendingRegistrationAcceptance,
  submitDeferredAcceptance,
  confirmUserAgreement,
  isTrialSignup: isTrialSignupProp,
  leadId: leadIdProp,
  registrationTrial,
  registrationLeadId,
}) => {
  const { signInFormBg, primaryHeaderText } = branding.colors;
  const urlTrial = parseTrialSignupParams();
  const isTrialSignup = isTrialSignupProp || registrationTrial || urlTrial.isTrial;
  const leadId = leadIdProp || registrationLeadId || urlTrial.leadId;

  const [editedState, setEditedState] = useState({});
  const [extraDetails, setExtraDetails] = useState(false);
  const [error, setError] = useState(false);
  const [acceptTNCs, setAcceptTNCs] = useState(false);
  const [showSLA, setShowSLA] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const updateField = (e) => {
    const newEditedState = { ...editedState };
    newEditedState[e.target.name] = e.target.value;
    setEditedState(newEditedState);
  };

  const confirmSignUp = async () => {
    await signupValidationSchema
      .validate({ acceptTNCs: acceptTNCs }, { abortEarly: false })
      .then(async () => {
        try {
          loggingIn(true);
          await amplifyConfirmSignUp({
            username,
            confirmationCode: editedState['confirmationCode'],
          });
          await signIn({ username, password: password || newPassword });
          await getUserCredentials();
          const signInUser = await buildLoginPayload();

          let registrationUser = user;
          let registrationOrg = organisation;

          if (newConfirmationCode) {
            //In case user resend confirmation code
            const { family_name, given_name, email } = signInUser.attributes;

            registrationUser = {
              firstName: given_name,
              lastName: family_name,
              email: email,
              priceId: signInUser.attributes['custom:priceId'],
            };

            registrationOrg = isTrialSignup
              ? {
                  name: signInUser.attributes['custom:companyName'],
                  planId: urlTrial.planId || organisation?.planId || null,
                  fteBand: urlTrial.fteBand || organisation?.fteBand || null,
                  fte: urlTrial.fte ?? organisation?.fte ?? null,
                }
              : {
                  name: signInUser.attributes['custom:companyName'],
                  revenue: signInUser.attributes['custom:organisationRevenue'],
                  subscriptionPlan: signInUser.attributes['custom:subscriptionPlan'],
                  revenueRange: signInUser.attributes['custom:revenueRange'],
                  subscriptionPrice: signInUser.attributes['custom:subscriptionPrice'],
                  stripeCoupon: signInUser.attributes['custom:coupon'],
                  stripeCouponDurationMonths: signInUser.attributes['custom:couponDurationMonths'],
                  sripeCouponPercetOff: signInUser.attributes['custom:couponPercentOff'],
                };
          }

          newUserSubscription(cleanDeep(registrationUser), { ...cleanDeep(registrationOrg) }, {
            trial: isTrialSignup,
            leadId: isTrialSignup ? leadId : null,
          });
          await newUser(registrationUser, registrationOrg, {
            trial: isTrialSignup,
            leadId: isTrialSignup ? leadId : null,
          });

          // savedOrg from userRegistration is raw Dynamo — refetch for entitlements / trial fields
          await getOrganisation();

          if (acceptTNCs) {
            try {
              await submitDeferredAcceptance();
            } catch (e) {
              console.log('Deferred legal accept failed, falling back to legacy:', e);
              await confirmUserAgreement(true);
            }
          }

          loggingIn(false);
          setExtraDetails(true);
        } catch (error) {
          loggingIn(false);
          const code = error?.code || error?.response?.data?.error;
          setError({
            ...error,
            message: trialRegistrationErrorMessage(code, error?.log || error?.message),
            code,
          });
          console.log('Error confirming sign up', error);
        }
      })
      .catch((error) => {
        loggingIn(false);
        console.log('ValidationError', error);
        setValidationError(error.inner);
      });
  };

  if (extraDetails && currentOrganisation) {
    return <BillingDetails isTrialSignup={isTrialSignup} />;
  }

  return (
    <div className='login-body' style={{ height: '100vh', width: '100%', display: 'flex' }}>
      <LogoSide />
      <div className='signInSide' style={{ background: signInFormBg }}>
        <div className='confirmationSide' style={{ width: '35vw' }}>
          <h1 style={{ marginBottom: '1.5vh', color: primaryHeaderText }}>Confirmation Email</h1>
          <div className='col-xs-12 alert-danger alert' style={{ marginBottom: 0, display: error ? 'block' : 'none' }}>
            <h5 style={{ textAlign: 'left' }}>Error: {error?.log || error?.message}</h5>
          </div>

          <h5 style={{ display: 'block', fontSize: 15, color: primaryHeaderText, marginBottom: '1.5vh' }}>
            We've sent you a confirmation email...
          </h5>

          <Input
            type='text'
            className='mb-4 h-[5vh]'
            id='confirmCode-input'
            name='confirmationCode'
            placeholder='Paste your code here*'
            onChange={updateField}
          />

          <div className='acceptTNCs-checkbox'>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type='checkbox'
                id='Register-AcceptTerms'
                style={{ margin: 0 }}
                onChange={() => setAcceptTNCs(!acceptTNCs)}
              />
              <label style={{ fontWeight: 400, marginBottom: 0, marginLeft: 5 }}>
                By clicking you are accepting to our{' '}
                <button style={{ border: 0, background: 'inherit' }} onClick={() => setShowSLA(!showSLA)}>
                  Software License Agreement
                </button>{' '}
                and our{' '}
                <button
                  style={{ border: 0, background: 'inherit' }}
                  onClick={() => setShowPrivacyPolicy(!showPrivacyPolicy)}>
                  Privacy Policy
                </button>
                .
              </label>
            </div>
          </div>

          <ValidationMessage
            message={validationError && _.find(validationError, { path: 'softwareAgreementAccepted' })?.message}
          />

          <Button
            size='lg'
            style={{ color: 'white' }}
            className='w-full'
            disabled={processing ? true : false}
            onClick={confirmSignUp}>
            {processing && <Loader2 size={16} strokeWidth={1.5} className='animate-spin icon-spinner' />}
            Confirm
          </Button>

          {(showSLA || showPrivacyPolicy) && (
            <div>
              <RegistrationAgreements
                setShowSoftwareLicenseAgreement={setShowSLA}
                showSoftwareLicenseAgreement={showSLA}
                setShowPrivacyPolicy={setShowPrivacyPolicy}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    branding: state.branding,
    currentOrganisation: state.currentOrganisation,
    processing: state.authentication.processing,
    registrationTrial: state.profile?.registrationDetails?.trial === true,
    registrationLeadId: state.profile?.registrationDetails?.leadId || null,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getUserCredentials: () => dispatch(AUTHENTICATION_ACTIONS.getUserCredentials()),
    newUser: (user, organisation, options) => dispatch(AUTHENTICATION_ACTIONS.newUser(user, organisation, options)),
    loggingIn: (saving) => dispatch(AUTHENTICATION_ACTIONS.loggingIn(saving)),
    newUserSubscription: (user, organisation, meta) =>
      dispatch(AUTHENTICATION_ACTIONS.newUserSubscription(user, organisation, meta)),
    selectPrice: (price) => dispatch(PAYMENTACTIONS.selectPrice(price)),
    subscriptionPlan: (subscriptionPlan) => dispatch(PAYMENTACTIONS.subscriptionPlan(subscriptionPlan)),
    getOrganisation: () => dispatch(ORGANISATIONACTIONS.getOrganisation()),
    setPendingRegistrationAcceptance: (accepted) => dispatch(LEGAL_ACTIONS.setPendingRegistrationAcceptance(accepted)),
    submitDeferredAcceptance: () => dispatch(LEGAL_ACTIONS.submitDeferredAcceptance()),
    confirmUserAgreement: (inclOrg) => dispatch(AUTHENTICATION_ACTIONS.confirmUserAgreement(inclOrg)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfirmEmail);
